import { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useEffortsFlsTrend } from '../hooks/useEffortsDashboard';
import {
  FIRST_COL_SX,
  HEADER_CELL_SX,
  NUM_CELL_SX,
  TABLE_CARD_SX,
  TABLE_SX,
  TABLE_WRAP_SX,
  TableCardTitle,
  TableFooter,
} from './EffortsTableParts.jsx';
import {
  buildFyMonths,
  covTextColor,
  compTextColor,
  TENURE_BANDS,
  tenureInBand,
} from './effortsTableStyles.js';

const ROWS_PER_PAGE = 50;
const DASH = '—';

/** Coloured-text percentage cell (reference .cf-* text, not coloured cells). */
function pctText(value, color) {
  if (value == null || !Number.isFinite(Number(value))) return DASH;
  return (
    <Box component="span" sx={color ? { color, fontWeight: 800 } : undefined}>
      {Number(value).toFixed(1)}%
    </Box>
  );
}

export default function FlsCoverageTrendTab() {
  const [search, setSearch] = useState('');
  const [tenureBand, setTenureBand] = useState('all');
  const [page, setPage] = useState(0);

  const { data, isLoading, isError } = useEffortsFlsTrend();

  const employees = useMemo(() => data?.employees ?? [], [data]);

  // Always render the full FY (Apr→Mar); future/no-data months show "—".
  const months = useMemo(
    () => buildFyMonths(data?.fiscalYear, data?.months),
    [data?.fiscalYear, data?.months]
  );

  // Search (name OR code) + tenure band filter across ALL rows.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees.filter((e) => {
      if (!tenureInBand(e?.tenureYears, tenureBand)) return false;
      if (!q) return true;
      const name = String(e?.name ?? '').toLowerCase();
      const code = String(e?.employeeCode ?? '').toLowerCase();
      return name.includes(q) || code.includes(q);
    });
  }, [employees, search, tenureBand]);

  // Reset to page 1 whenever the dataset or the filters change.
  const lastKeyRef = useRef(null);
  const key = `${employees.length}|${search}|${tenureBand}`;
  if (lastKeyRef.current !== key) {
    lastKeyRef.current = key;
    if (page !== 0) setPage(0);
  }

  const pageCount = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
  const safePage = Math.min(page, pageCount - 1);
  const paged = filtered.slice(
    safePage * ROWS_PER_PAGE,
    safePage * ROWS_PER_PAGE + ROWS_PER_PAGE
  );

  if (isLoading) return <Alert severity="info">Loading FLS coverage trend…</Alert>;
  if (isError) return <Alert severity="error">Failed to load FLS coverage trend.</Alert>;
  if (employees.length === 0) {
    return <Alert severity="info">No FLS coverage trend data available.</Alert>;
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 2,
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#6B7280' }}>
            Tenure Band
          </Typography>
          <FormControl size="small">
            <Select
              value={tenureBand}
              onChange={(e) => {
                setTenureBand(e.target.value);
                setPage(0);
              }}
              sx={{ fontSize: '12px', minWidth: 150 }}
            >
              {TENURE_BANDS.map((t) => (
                <MenuItem key={t.value} value={t.value} sx={{ fontSize: '12px' }}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <TextField
          size="small"
          placeholder="Search name / code…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {filtered.length === 0 ? (
        <Alert severity="info">No FLS match the current filter / tenure band.</Alert>
      ) : (
        <Box sx={TABLE_CARD_SX}>
          <TableCardTitle
            title="FLS — Doctor Coverage % &amp; Freq. Compliance % Trend"
            count={filtered.length}
            countLabel="FLS"
          />
          <Box sx={{ fontSize: '12px', color: '#6B7280', mb: 1.5, mt: -0.5 }}>
            {filtered.length} FLS · monthly Dr. Coverage % and Visit Freq. Compliance %
          </Box>
          <TableContainer sx={{ ...TABLE_WRAP_SX, maxHeight: 560 }}>
            <Table size="small" stickyHeader sx={TABLE_SX}>
              <TableHead>
                <TableRow>
                  <TableCell rowSpan={2} sx={HEADER_CELL_SX}>Empl. Code</TableCell>
                  <TableCell rowSpan={2} sx={HEADER_CELL_SX}>Empl. Name</TableCell>
                  <TableCell rowSpan={2} sx={HEADER_CELL_SX}>Date of Joining</TableCell>
                  <TableCell rowSpan={2} align="right" sx={HEADER_CELL_SX}>Tenure in Years</TableCell>
                  <TableCell rowSpan={2} sx={HEADER_CELL_SX}>MIS HQ</TableCell>
                  {months.map((m) => (
                    <TableCell
                      key={m.yearmonth}
                      colSpan={2}
                      align="center"
                      sx={HEADER_CELL_SX}
                    >
                      {m.label}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  {months.map((m) => [
                    <TableCell key={`${m.yearmonth}-cov`} align="right" sx={HEADER_CELL_SX}>
                      Dr. Covg %
                    </TableCell>,
                    <TableCell key={`${m.yearmonth}-comp`} align="right" sx={HEADER_CELL_SX}>
                      Freq. Compl %
                    </TableCell>,
                  ])}
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.map((emp) => {
                  const byMonth = Object.fromEntries(
                    (emp.points ?? []).map((p) => [String(p.yearmonth), p])
                  );
                  return (
                    <TableRow key={emp.employeeCode ?? emp.name} hover>
                      <TableCell sx={FIRST_COL_SX}>{emp.employeeCode ?? DASH}</TableCell>
                      <TableCell sx={{ ...FIRST_COL_SX, whiteSpace: 'nowrap' }}>
                        {emp.name ?? DASH}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{emp.joiningDate ?? DASH}</TableCell>
                      <TableCell align="right" sx={NUM_CELL_SX}>
                        {emp.tenureYears == null ? DASH : Number(emp.tenureYears).toFixed(1)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{emp.misHq ?? DASH}</TableCell>
                      {months.map((m) => {
                        const cov = byMonth[m.yearmonth]?.coveragePercent;
                        const comp = byMonth[m.yearmonth]?.compliancePercent;
                        return [
                          <TableCell key={`${m.yearmonth}-cov`} align="right" sx={NUM_CELL_SX}>
                            {pctText(cov, covTextColor(cov))}
                          </TableCell>,
                          <TableCell key={`${m.yearmonth}-comp`} align="right" sx={NUM_CELL_SX}>
                            {pctText(comp, compTextColor(comp))}
                          </TableCell>,
                        ];
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TableFooter
            page={safePage}
            pageSize={ROWS_PER_PAGE}
            total={filtered.length}
            onPageChange={setPage}
          />
        </Box>
      )}
    </Box>
  );
}
